import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Input } from '../../../ui/input';
import { Button } from '../../../ui/button';
import { Spinner } from '../../../ui/spinner';
import { EmptyState } from '../../../ui/empty-state';
import { PageHeader, PageHeaderTitle, PageHeaderDescription, PageHeaderActions } from '../../../ui/page-header';
import { Breadcrumb } from '../../../ui/breadcrumb';
import { IconSearch, IconRefresh, IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { WorkflowProvider, useWorkflow } from '../../../../contexts/WorkflowContext';
import StageColumn from './StageColumn';
import SettingsMenu from './SettingsMenu';
import EditCampaignModal from './EditCampaignModal';
import MobileStageNavigation from './MobileStageNavigation';
import MobileCampaignList from './MobileCampaignList';
import CustomDragLayer from './CustomDragLayer';
import CreateCampaignModal from './CreateCampaignModal';
import { useAuth } from '../../../../contexts/auth/AuthContext';
import agencyOverviewService from '../../../../services/agencyOverviewService';
import socketService from '../../../../services/socketService';

const EMPTY_ARRAY = [];
const STAGE_ORDER = ['waiting_to_start', 'recruitment', 'live', 'payment_report', 'finished'];

/**
 * WorkflowDashboard Component - Migrated to shadcn/Tailwind
 */

const WorkflowDashboardContent = () => {
 const navigate = useNavigate();
 const [searchParams, setSearchParams] = useSearchParams();
 const isMobile = useMediaQuery('(max-width: 1279px)');
 const { hasPermission } = useAuth();
 const canCreateCampaign = hasPermission('manage_campaigns');

 const { stages, counts, loading, error, fetchCampaigns, isConnected } = useWorkflow();

 const [searchQuery, setSearchQuery] = useState('');
 const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
 const [selectedCampaign, setSelectedCampaign] = useState(null);
 const [editModalOpen, setEditModalOpen] = useState(false);
 const [campaignToEdit, setCampaignToEdit] = useState(null);
 const [summaries, setSummaries] = useState({});
 const [summaryRevision, setSummaryRevision] = useState(0);
 const createModalOpen = canCreateCampaign && searchParams.get('action') === 'create';

 // Mobile-specific state — initialize from URL param if present
 const stageParam = searchParams.get('stage');
 const [activeStage, setActiveStage] = useState(
 stageParam && STAGE_ORDER.includes(stageParam) ? stageParam : 'waiting_to_start'
 );

 const campaignIds = useMemo(() => STAGE_ORDER.flatMap((stage) => stages[stage] || []).map((campaign) => campaign._id), [stages]);
 const campaignIdsKey = campaignIds.join(',');

 useEffect(() => {
 const controller = new AbortController();
 if (!campaignIds.length) {
 setSummaries({});
 return () => controller.abort();
 }
 agencyOverviewService.getCampaignSummaries(campaignIds, { signal: controller.signal })
 .then((response) => {
 const rows = Array.isArray(response) ? response : response?.summaries || [];
 if (!controller.signal.aborted) setSummaries(Object.fromEntries(rows.map((summary) => [String(summary.campaignId), summary])));
 })
 .catch((err) => {
 if (err.name !== 'CanceledError' && err.name !== 'AbortError') console.error('Error loading Campaign summaries:', err);
 });
 return () => controller.abort();
 // The stable ID string avoids refetching when stage arrays are recreated with the same records.
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [campaignIdsKey, summaryRevision]);

 useEffect(() => {
 let timer;
 const invalidate = () => {
 clearTimeout(timer);
 timer = setTimeout(() => setSummaryRevision((revision) => revision + 1), 250);
 };
 const unsubscribe = socketService.on('agency:changed', invalidate);
 return () => {
 clearTimeout(timer);
 if (typeof unsubscribe === 'function') unsubscribe();
 };
 }, []);

 // Filter campaigns by search query
 const filteredStages = useMemo(() => {
 if (!searchQuery.trim()) return stages;

 const query = searchQuery.toLowerCase();
 const filtered = {};

 Object.keys(stages).forEach(stageKey => {
 filtered[stageKey] = stages[stageKey].filter(campaign => {
 const nameMatch = campaign.name?.toLowerCase().includes(query);
 const brandMatch = campaign.brandId?.name?.toLowerCase().includes(query);
 return nameMatch || brandMatch;
 });
 });

 return filtered;
 }, [stages, searchQuery]);

 // Stable per-stage campaign arrays to avoid breaking React.memo
 const stageCampaigns = useMemo(() => {
 const result = {};
 STAGE_ORDER.forEach(s => {
 result[s] = (filteredStages[s] || EMPTY_ARRAY).map((campaign) => ({
 ...campaign,
 operationalSummary: summaries[String(campaign._id)]
 }));
 });
 return result;
 }, [filteredStages, summaries]);

 // Memoized handlers to prevent StageColumn re-renders
 const handleCampaignClick = useCallback((campaign) => {
 navigate(`/campaigns/${campaign._id}`);
 }, [navigate]);

 const handleSettingsClick = useCallback((campaign, event) => {
 if (!canCreateCampaign) return;
 setSelectedCampaign(campaign);
 setSettingsAnchorEl(event.currentTarget);
 }, [canCreateCampaign]);

 const handleSettingsClose = useCallback(() => {
 setSettingsAnchorEl(null);
 }, []);

 const handleEditClick = useCallback((campaign) => {
 setCampaignToEdit(campaign);
 setEditModalOpen(true);
 }, []);

 const handleEditClose = useCallback(() => {
 setEditModalOpen(false);
 setCampaignToEdit(null);
 }, []);

 const handleEditSuccess = useCallback(() => {
 setEditModalOpen(false);
 setCampaignToEdit(null);
 fetchCampaigns();
 }, [fetchCampaigns]);

 const handleRefresh = useCallback(() => {
 fetchCampaigns();
 }, [fetchCampaigns]);

 const handleCreateOpen = useCallback(() => {
 const nextParams = new URLSearchParams(searchParams);
 nextParams.set('action', 'create');
 setSearchParams(nextParams);
 }, [searchParams, setSearchParams]);

 const handleCreateClose = useCallback(() => {
 const nextParams = new URLSearchParams(searchParams);
 nextParams.delete('action');
 setSearchParams(nextParams, { replace: true });
 }, [searchParams, setSearchParams]);

 const handleCreateSuccess = useCallback(() => {
 fetchCampaigns();
 }, [fetchCampaigns]);

 // Loading state
 if (loading && !stages.waiting_to_start.length) {
 return (
 <>
 <div className="flex flex-col items-center justify-center gap-6 min-h-[400px]">
 <Spinner size="lg" className="text-text-tertiary" />
 <p className="text-sm font-medium text-text-tertiary">Loading campaigns...</p>
 </div>
 <CreateCampaignModal
 open={createModalOpen}
 onClose={handleCreateClose}
 onSuccess={handleCreateSuccess}
 />
 </>
 );
 }

 // Error state
 if (error) {
 return (
 <>
 <EmptyState
 icon={IconAlertCircle}
 title="Failed to load campaigns"
 description={error}
 action={
 <Button variant="ghost" size="sm" onClick={handleRefresh} className="bg-accent/10 text-accent hover:bg-accent/15">
 <IconRefresh size={16} strokeWidth={2} className="mr-1.5" />
 Retry
 </Button>
 }
 />
 <CreateCampaignModal
 open={createModalOpen}
 onClose={handleCreateClose}
 onSuccess={handleCreateSuccess}
 />
 </>
 );
 }

 // ============================================
 // MOBILE VIEW
 // ============================================
 const mobileContent = (
 <div className="h-full flex flex-col overflow-hidden">
 {/* Mobile Header */}
 <div className="flex-shrink-0 flex items-center justify-between px-3 pt-3 pb-2 gap-3">
 <div className="flex-1 min-w-0">
 <h5 className="text-2xl font-semibold truncate text-text-primary tracking-tight">
 Workflow
 </h5>
 {!isConnected && (
 <span className="text-xs font-medium text-warning">Disconnected</span>
 )}
 </div>
 <div className="flex gap-2">
 {canCreateCampaign && <Button
 size="icon"
 onClick={handleCreateOpen}
 className="h-11 w-11"
 aria-label="New Campaign"
 title="New Campaign"
 >
 <IconPlus size={20} strokeWidth={2} />
 </Button>}
 <Button
 variant="ghost"
 size="icon"
 onClick={handleRefresh}
 disabled={loading}
 className="h-10 w-10 border border-glass-border bg-surface-1 text-text-secondary"
 aria-label="Refresh campaigns"
 title="Refresh campaigns"
 >
 <IconRefresh size={20} strokeWidth={2} className={loading ? 'animate-spin' : ''} />
 </Button>
 </div>
 </div>

 {/* Search Bar - Mobile */}
 <div className="flex-shrink-0 px-3 pb-2">
 <div className="relative">
 <IconSearch size={20} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
 <Input
 placeholder="Search campaigns..."
 aria-label="Search campaigns"
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="pl-10"
 />
 </div>
 </div>

 {/* Stage Navigation Tabs */}
 <div className="flex-shrink-0">
 <MobileStageNavigation
 activeStage={activeStage}
 onStageChange={setActiveStage}
 counts={counts}
 />
 </div>

 {/* Campaign List for Active Stage */}
 <div className="flex-1 min-h-0 overflow-hidden">
 <MobileCampaignList
 stage={activeStage}
 campaigns={filteredStages[activeStage] || []}
 summaries={summaries}
 onCampaignClick={handleCampaignClick}
 onCampaignSettings={handleSettingsClick}
 canManageCampaigns={canCreateCampaign}
 />
 </div>

 {/* Modals */}
 {canCreateCampaign && <EditCampaignModal
 open={editModalOpen}
 onClose={handleEditClose}
 onSuccess={handleEditSuccess}
 campaign={campaignToEdit}
 />}
 <CreateCampaignModal
 open={createModalOpen}
 onClose={handleCreateClose}
 onSuccess={handleCreateSuccess}
 />

 {/* Settings Menu - Shared with desktop */}
 {canCreateCampaign && selectedCampaign && (
 <SettingsMenu
 anchorEl={settingsAnchorEl}
 open={Boolean(settingsAnchorEl)}
 onClose={handleSettingsClose}
 campaign={selectedCampaign}
 onEditClick={handleEditClick}
 />
 )}
 </div>
 );

 // ============================================
 // DESKTOP VIEW (WITH DRAG-AND-DROP)
 // ============================================
 const desktopContent = (
 <div className="h-full flex flex-col overflow-hidden">
 {/* Breadcrumb */}
 <Breadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Workflow' }]} className="mb-4" />

 {/* Header */}
 <PageHeader className="mb-4 sm:flex-col sm:items-stretch lg:flex-row lg:items-center">
 <div>
 <PageHeaderTitle>Campaign Workflow</PageHeaderTitle>
 <div className="flex items-center gap-3">
 <PageHeaderDescription>Track campaigns through each stage</PageHeaderDescription>
 {!isConnected && (
 <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-warning-muted">
 <div className="w-1.5 h-1.5 bg-warning" />
 <span className="text-xs font-medium text-warning">Disconnected</span>
 </div>
 )}
 </div>
 </div>
 <PageHeaderActions className="w-full flex-wrap lg:w-auto lg:flex-nowrap">
 <div className="relative min-w-[180px] flex-1 lg:w-[280px] lg:flex-none">
 <IconSearch size={18} strokeWidth={2} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" />
 <Input
 placeholder="Search campaigns..."
 aria-label="Search campaigns"
 value={searchQuery}
 onChange={(e) => setSearchQuery(e.target.value)}
 className="pl-10 h-9"
 />
 </div>
 {canCreateCampaign && <Button size="sm" onClick={handleCreateOpen}>
 <IconPlus size={18} strokeWidth={2} className="mr-1.5" />
 New Campaign
 </Button>}
 <Button
 variant="ghost"
 size="icon"
 onClick={handleRefresh}
 disabled={loading}
 className="h-9 w-9 border border-glass-border bg-surface-1 text-text-secondary"
 aria-label="Refresh campaigns"
 title="Refresh campaigns"
 >
 <IconRefresh size={18} strokeWidth={2} className={loading ? 'animate-spin' : ''} />
 </Button>
 </PageHeaderActions>
 </PageHeader>

 {/* Workflow Columns */}
 <div role="region" aria-label="Campaign stage board" tabIndex={0} className="flex-1 flex overflow-x-auto overflow-y-hidden gap-3 pb-4 min-h-0 min-w-0 focus-visible:outline-accent">
 {STAGE_ORDER.map(stage => (
 <StageColumn
 key={stage}
 stage={stage}
 campaigns={stageCampaigns[stage]}
 count={counts[stage] || 0}
 onCampaignClick={handleCampaignClick}
 onCampaignSettings={handleSettingsClick}
 canManageCampaigns={canCreateCampaign}
 />
 ))}
 </div>

 {/* Settings Menu */}
 {canCreateCampaign && selectedCampaign && (
 <SettingsMenu
 anchorEl={settingsAnchorEl}
 open={Boolean(settingsAnchorEl)}
 onClose={handleSettingsClose}
 campaign={selectedCampaign}
 onEditClick={handleEditClick}
 />
 )}

 {/* Edit Campaign Modal */}
 {canCreateCampaign && <EditCampaignModal
 open={editModalOpen}
 onClose={handleEditClose}
 onSuccess={handleEditSuccess}
 campaign={campaignToEdit}
 />}
 <CreateCampaignModal
 open={createModalOpen}
 onClose={handleCreateClose}
 onSuccess={handleCreateSuccess}
 />
 </div>
 );

 // Always mount DndProvider to avoid tree remount on resize
 // CampaignCard uses useDrag() so DndProvider must wrap both mobile and desktop
 return (
 <DndProvider backend={HTML5Backend}>
 {isMobile ? mobileContent : desktopContent}
 {!isMobile && <CustomDragLayer />}
 </DndProvider>
 );
};

// Main component wrapped with provider
const WorkflowDashboard = () => {
 return (
 <WorkflowProvider>
 <div className="p-3 sm:p-8 overflow-hidden" style={{ height: 'calc(100vh - 64px)' }}>
 <WorkflowDashboardContent />
 </div>
 </WorkflowProvider>
 );
};

export default WorkflowDashboard;
